// /portal/jobs/:id — the page a client lands on when they tap a job
// card or click an "Open in portal" link from a notification email.
//
// Data: single .maybeSingle() against portal_jobs. RLS already gates
// the row to the caller's company; cross-company or deleted jobs
// resolve to NULL → "Job not found" empty state. The empty state IS
// the security working, not an error.
//
// Mobile-first: this page opens on a phone more often than not. The
// hero block stacks vertically; the photo grid scales to viewport
// width via CSS grid auto-fill so 80–120px thumbs sit naturally on
// any breakpoint.

import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Calendar, MapPin, User as UserIcon, Phone, ExternalLink, Briefcase, Image as ImageIcon,
  Truck, MapPinned, TimerReset, RotateCcw, CheckCircle2,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { mapsUrl } from '@/lib/address'
import { JOB_STATUS_CONFIG } from '@/pages/field/FieldHub'
import { listJobPhotos, getJobPhotoSignedUrl, type JobPhoto } from '@/lib/jobPhotos'

interface PortalJob {
  id: string
  job_number: string | null
  title: string | null
  status: string
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_line2: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  job_notes: string | null
  assigned_agent_name: string | null
  csr_name: string | null
}

export default function PortalJobDetail() {
  const { id } = useParams<{ id: string }>()

  const { data: job, isLoading, isError } = useQuery({
    queryKey: ['portal-job-detail', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('portal_jobs')
        .select('id, job_number, title, status, scheduled_date, scheduled_start, scheduled_end, address_line1, address_line2, address_city, address_state, address_zip, job_notes, assigned_agent_name, csr_name')
        .eq('id', id)
        .maybeSingle()
      if (error) throw error
      return data as PortalJob | null
    },
    enabled: !!id,
  })

  if (isLoading) {
    return <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'center', padding: '32px 0' }}>Loading…</p>
  }

  if (isError || !job) {
    // Empty state == security working. No row-not-found error dump for
    // a portal user; they just see a clean "not found" with a back link.
    return (
      <div style={{ padding: '40px 16px', textAlign: 'center' }}>
        <Briefcase style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>Job not found</p>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
          It may have been removed, or you may not have access to this job.
        </p>
        <Link to="/portal/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', textDecoration: 'none' }}>
          <ArrowLeft style={{ width: 12, height: 12 }} /> Back to Jobs
        </Link>
      </div>
    )
  }

  return <JobDetailBody job={job} />
}

function JobDetailBody({ job }: { job: PortalJob }) {
  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
  const address = useMemo(() => composeAddress(job), [job])
  const schedule = useMemo(() => composeSchedule(job), [job])
  const directionsUrl = useMemo(() => mapsUrl({
    line1: job.address_line1, line2: job.address_line2,
    city: job.address_city, state: job.address_state, zip: job.address_zip,
  }), [job])

  // Status-derived back destination. Completed jobs route the back
  // link to History; everything else to Jobs. Direct arrivals (email
  // landing on this page cold) get a sensible destination.
  const backTo = job.status === 'complete' ? '/portal/history' : '/portal/jobs'
  const backLabel = job.status === 'complete' ? 'Back to History' : 'Back to Jobs'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to={backTo}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--ink-tertiary)', textDecoration: 'none', alignSelf: 'flex-start' }}>
        <ArrowLeft style={{ width: 12, height: 12 }} /> {backLabel}
      </Link>

      {/* Header */}
      <header style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number ?? ''}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {cfg.label}
          </span>
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{job.title ?? 'Untitled'}</h1>
      </header>

      {/* Schedule */}
      {schedule && (
        <Block title="Schedule">
          <Row icon={<Calendar style={iconStyle} />} text={schedule} />
        </Block>
      )}

      {/* Location + directions */}
      {address && (
        <Block title="Location">
          <Row icon={<MapPin style={iconStyle} />} text={address} />
          {directionsUrl && (
            <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '7px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
              <ExternalLink style={{ width: 12, height: 12 }} />
              Get directions
            </a>
          )}
        </Block>
      )}

      {/* People */}
      {(job.assigned_agent_name || job.csr_name) && (
        <Block title="People">
          {job.assigned_agent_name && <Row icon={<UserIcon style={iconStyle} />} text={`Tech: ${job.assigned_agent_name}`} />}
          {job.csr_name && <Row icon={<Phone style={iconStyle} />} text={`Coordinator: ${job.csr_name}`} />}
        </Block>
      )}

      {/* Activity — vendor lifecycle. Null-renders entirely when there
          are no events on the latest-accepted assignment; pixel-equivalent
          to pre-V2f for jobs without vendor activity. The portal_job_events
          view enforces the column budget: note is structurally absent,
          delayed events surface as a generic "Running behind" with no
          freeform text. Reads through the new portal_read policy on
          vendor_assignment_events (workspace + company chain). */}
      <ActivityBlock jobId={job.id} />

      {/* Details */}
      {job.job_notes && job.job_notes.trim() && (
        <Block title="Details">
          <p style={{ fontSize: 14, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{job.job_notes}</p>
        </Block>
      )}

      {/* Photo grid */}
      <PhotoGrid jobId={job.id} />
    </div>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  )
}

function Row({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--ink-primary)' }}>
      {icon}
      <span>{text}</span>
    </div>
  )
}

const iconStyle: React.CSSProperties = { width: 14, height: 14, color: 'var(--ink-tertiary)', flexShrink: 0 }

// ─── Photo grid (detail-page variant) ───────────────────────────────
// PortalPhotoStrip is the strip used by cards. The detail page has
// room for a tighter grid (3-up on phones, 4-up on tablets+) so each
// thumb is a bit larger and the visual rhythm matches the longer
// content. Same signed-URL helper, same RLS gate.
function PhotoGrid({ jobId }: { jobId: string }) {
  const { data: photos = [] } = useQuery({
    queryKey: ['portal-job-photos', jobId],
    queryFn: async () => {
      const { data, error } = await listJobPhotos(jobId)
      if (error) throw error
      return data
    },
  })
  if (photos.length === 0) return null
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ImageIcon style={{ width: 11, height: 11 }} /> Photos ({photos.length})
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
        {photos.map(p => <GridThumbnail key={p.id} photo={p} />)}
      </div>
    </div>
  )
}

function GridThumbnail({ photo }: { photo: JobPhoto }) {
  const { data: url } = useQuery({
    queryKey: ['portal-photo-url', photo.storage_path],
    queryFn: async () => {
      const { url, error } = await getJobPhotoSignedUrl(photo.storage_path, 300)
      if (error || !url) throw error ?? new Error('No signed URL')
      return url
    },
    staleTime: 290_000,
    gcTime: 290_000,
  })
  return (
    <a href={url ?? undefined} target="_blank" rel="noopener noreferrer"
      aria-label={photo.caption ?? 'Photo'}
      style={{ aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)', display: 'block' }}>
      {url && <img src={url} alt={photo.caption ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
    </a>
  )
}

// ─── helpers ────────────────────────────────────────────────────────
function composeAddress(j: PortalJob): string | null {
  const street = [j.address_line1, j.address_line2].filter(Boolean).join(', ')
  const region = [j.address_city, j.address_state].filter(Boolean).join(', ')
  const full = [street, region, j.address_zip].filter(Boolean).join(' · ')
  return full || null
}
function composeSchedule(j: PortalJob): string | null {
  if (!j.scheduled_date && !j.scheduled_start) return null
  const parts: string[] = []
  if (j.scheduled_date) parts.push(formatDate(j.scheduled_date, 'EEEE, MMM d, yyyy'))
  if (j.scheduled_start && j.scheduled_end) parts.push(`${j.scheduled_start}–${j.scheduled_end}`)
  else if (j.scheduled_start) parts.push(j.scheduled_start)
  return parts.join(' · ')
}

// ─── Activity block — vendor lifecycle for the client ────────────────
//
// Reads through portal_job_events (security_invoker view scoped to the
// latest-accepted assignment per job; note column structurally absent).
// Newest-first. Renders nothing when no events — null preserves the
// pre-V2f layout for jobs without vendor activity.
//
// Copy rules:
//   * en_route   → "Technician on the way"
//   * on_site    → "Technician on site"
//   * delayed    → "Running behind" — GENERIC, no freeform text. The
//                  vendor's note never reaches the client surface.
//   * return_visit_scheduled → "Return visit scheduled · {date}" when
//                  scheduled_return_date is present, else generic copy.
//   * completed  → "Work complete" (in case the staff status hasn't
//                  caught up yet — the V2e banner is the staff-side
//                  affordance to flip the job's own status).
type PortalLifecycleEvent =
  | 'en_route' | 'on_site' | 'delayed' | 'return_visit_scheduled' | 'completed'

interface PortalJobEvent {
  field_job_id: string
  event_type: PortalLifecycleEvent
  created_at: string
  scheduled_return_date: string | null
}

interface ActivityVisual { label: string; icon: typeof Truck; color: string; bg: string; border: string }

const ACTIVITY_COPY: Record<PortalLifecycleEvent, ActivityVisual> = {
  en_route:               { label: 'Technician on the way', icon: Truck,        color: 'var(--info)',     bg: 'var(--info-dim)',     border: 'var(--info-border)' },
  on_site:                { label: 'Technician on site',    icon: MapPinned,    color: 'var(--info)',     bg: 'var(--info-dim)',     border: 'var(--info-border)' },
  delayed:                { label: 'Running behind',        icon: TimerReset,   color: 'var(--warning)',  bg: 'var(--warning-dim)',  border: 'var(--warning-border)' },
  return_visit_scheduled: { label: 'Return visit scheduled', icon: RotateCcw,    color: 'var(--accent-text)', bg: 'var(--accent-dim)', border: 'var(--accent-border)' },
  completed:              { label: 'Work complete',         icon: CheckCircle2, color: 'var(--positive)', bg: 'var(--positive-dim)', border: 'var(--positive-border)' },
}

function relativeWhen(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60_000) return 'just now'
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
    return `${Math.floor(ms / 86_400_000)}d ago`
  } catch { return '' }
}

function ActivityBlock({ jobId }: { jobId: string }) {
  const { data: events = [] } = useQuery<PortalJobEvent[]>({
    queryKey: ['portal-job-events', jobId],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_job_events')
        .select('field_job_id, event_type, created_at, scheduled_return_date')
        .eq('field_job_id', jobId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as PortalJobEvent[]
    },
  })
  if (events.length === 0) return null
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Activity</p>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {events.map(ev => {
          const c = ACTIVITY_COPY[ev.event_type]
          const sub = ev.event_type === 'return_visit_scheduled' && ev.scheduled_return_date
            ? `On ${ev.scheduled_return_date}`
            : null
          return (
            <li key={`${ev.created_at}-${ev.event_type}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: c.bg, border: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <c.icon style={{ width: 13, height: 13, color: c.color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--ink-primary)' }}>{c.label}</p>
                {sub && <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-secondary)' }}>{sub}</p>}
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>{relativeWhen(ev.created_at)}</p>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
